import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  ClientErrorResponse,
  Conversation,
  ConversationContext,
  ConversationParticipant,
  StoredConversation,
  InProgressResponse,
} from '@stripe-internal/conversations-client';
import {useClient, useWidget} from '@stripe-internal/conversations-ui';
import {ConfigContext} from '../../Providers/ConfigProvider';
import {getInProgressStoredConversation} from '../../helpers/getStoredConversation';
import {useProfile} from '../../Providers/ProfileProvider';
import {isStartFreshTriggerPoint} from '../../helpers/triggerPoints';

export const useConversation = ({
  conversationId,
  context,
  inProgressResponse: existingInProgressResponse,
}: {
  conversationId?: string;
  context?: ConversationContext;
  inProgressResponse?: InProgressResponse;
} = {}) => {
  const ref = useRef<boolean>(false);
  const {audience, entrypoint} = useContext(ConfigContext);
  const {profile} = useProfile();
  const widget = useWidget();
  const {visitor, featureFlags} = profile;
  const {enableStartUniversalHelpConversationImmediately} = featureFlags;
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [error, setError] = useState<boolean>(false);
  const [sentryEventId, setSentryEventId] = useState<string | undefined>();
  const previousTriggerPointRef = useRef<string | undefined>();

  const honorExistingInProgress = useRef<boolean>(!!existingInProgressResponse);

  const client = useClient();

  const participant: ConversationParticipant = useMemo(() => {
    return {
      id: visitor.id,
      role: 'user',
      displayName: visitor.displayName || 'User',
    };
  }, [visitor]);

  const resumeOrStartConversation = useCallback(
    async ({
      initialQuery,
      restart = false,
    }: {
      initialQuery?: string;
      restart?: boolean;
    } = {}) => {
      if (restart && !conversation?.isTabMostRecent()) {
        return;
      }

      try {
        let storedConversation: StoredConversation | undefined | null = null;
        let inProgressResponse = honorExistingInProgress.current
          ? existingInProgressResponse
          : undefined;
        const hasInProgress =
          inProgressResponse &&
          !('error' in inProgressResponse) &&
          inProgressResponse.data.inProgress &&
          honorExistingInProgress.current;

        honorExistingInProgress.current = false;

        if (!restart && !hasInProgress) {
          const result = await getInProgressStoredConversation({
            client,
            entryPoint: context?.entryPoint || entrypoint,
          });
          if (result) {
            storedConversation = result.storedConversation;
            inProgressResponse = result.inProgressResponse;
          }
        }

        // we don't need to reference any previous context.triggerParams here as
        // they are always from some triggerPoint; not a previous conversation's context
        let triggerParams = widget.triggerParams() ?? undefined;

        if (triggerParams && initialQuery) {
          triggerParams.initialQuery = initialQuery;
        } else if (restart) {
          triggerParams = {
            triggerPoint:
              previousTriggerPointRef.current || 'assistant_drawer_internal',
            initialQuery,
          };
          // This prevents the chat ended notice from briefly appearing while the widget is closing
          conversation?.setContextValue('supressChatEndedNotice', true);
        }

        const contextParams: ConversationContext = {
          userId: participant.id,
          audience,
          entryPoint: entrypoint,
          triggerParams,
          ...context,
        };

        // need to preserve the active and interacted tabs
        if (restart && conversation) {
          contextParams.tabs = conversation.tabs;
        }

        // reuse the existing stored conversation id if we have it
        // if undefined, the client will create a new conversation
        let conversationIdToAttachTo = restart
          ? undefined
          : storedConversation?.id || conversationId;

        if (
          restart ||
          (isStartFreshTriggerPoint(triggerParams?.triggerPoint) &&
            enableStartUniversalHelpConversationImmediately)
        ) {
          const tempResp = await client.startConversation({
            participant,
            context: {
              ...contextParams,
              placeholderConversation: true,
            },
          });

          if (!('error' in tempResp)) {
            const tempConversation = tempResp.data.conversation;
            setConversation(tempConversation);

            if (conversation) {
              await conversation.leave();
            } else {
              const prevConversationResp = await client.inProgress({
                context: {
                  ...contextParams,
                  channel: 'bot',
                },
              });
              if (!('error' in prevConversationResp)) {
                if (
                  prevConversationResp.data.inProgress &&
                  prevConversationResp.data.conversationId
                ) {
                  const {conversationId: prevConversationId} =
                    prevConversationResp.data;
                  await client.leaveConversation({
                    conversationId: prevConversationId,
                    participant,
                  });

                  if (conversationIdToAttachTo === prevConversationId) {
                    // if there was a stored conversation, but we just ended it
                    // don't try to use it as the new conversation's id
                    conversationIdToAttachTo = undefined;
                  }
                }
              }
            }
          }
        }

        const startContext = restart
          ? {}
          : {
              ...storedConversation?.data?.context,
              ...context,
            };

        const resp = await client.startConversation({
          conversationId: conversationIdToAttachTo,
          participant,
          context: {
            userId: participant.id,
            audience,
            entryPoint: entrypoint,
            triggerParams,
            ...startContext,
          },
          inProgressResponse,
        });
        if ('error' in resp) {
          const errorResponse = resp as ClientErrorResponse;
          setError(true);
          setSentryEventId(errorResponse.sentryEventId);
          return;
        }
        const newConversation = resp.data.conversation;
        setConversation(newConversation);
        previousTriggerPointRef.current =
          newConversation.context.triggerParams?.triggerPoint;
      } catch (e) {
        setError(true);
      }
    },
    [
      audience,
      client,
      context,
      conversation,
      conversationId,
      enableStartUniversalHelpConversationImmediately,
      entrypoint,
      existingInProgressResponse,
      participant,
      widget,
    ],
  );

  const restartConversation = useCallback(
    async ({initialQuery}: {initialQuery?: string} = {}) => {
      return resumeOrStartConversation({
        initialQuery,
        restart: true,
      });
    },
    [resumeOrStartConversation],
  );

  useEffect(() => {
    if (!context?.audience && !audience) {
      return;
    }

    if (!conversation && !ref.current) {
      ref.current = true;
      resumeOrStartConversation();
    }
  }, [audience, context, conversation, resumeOrStartConversation]);

  return {conversation, error, sentryEventId, restartConversation};
};
